import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Unified Shopify Connection panel.
 *
 * One client = ONE of two modes (never both):
 *   - "agency"     → pick stores from the agency-wide Shopify connection pool
 *   - "individual" → enter shop domain + per-client Admin API token (Fernet
 *                    encrypted at rest); falls back to nothing if not provided.
 *
 * Default = "agency" — preserves existing behaviour for every client.
 */
const ShopifyConnection = ({
  clientId,
  isEdit,
  formData,
  setFormData,
  shopifyStores: agencyPool = [],
  currency = "INR",
  readOnly = false,
}) => {
  const [overrideStatus, setOverrideStatus] = useState(null);
  const [loadingOverride, setLoadingOverride] = useState(isEdit);
  const [mode, setMode] = useState("agency"); // "agency" | "individual"

  const [newShop, setNewShop] = useState(""); // free-text input for Individual mode
  const [editingTokens, setEditingTokens] = useState({}); // {shop: plainToken}
  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // {ok, msg} | null
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const loadOverride = useCallback(async () => {
    if (!isEdit || !clientId) return;
    setLoadingOverride(true);
    try {
      const r = await axios.get(`${API}/clients/${clientId}/shopify/credentials-override`);
      setOverrideStatus(r.data);
      setMode(r.data?.enabled ? "individual" : "agency");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load Shopify connection status");
    } finally {
      setLoadingOverride(false);
    }
  }, [clientId, isEdit]);

  useEffect(() => { loadOverride(); }, [loadOverride]);

  const updateFormStores = (next) => {
    const fxCopy = { ...(formData.store_fx_rates || {}) };
    // drop FX rates for removed shops
    Object.keys(fxCopy).forEach(k => { if (!next.includes(k)) delete fxCopy[k]; });
    setFormData(prev => ({
      ...prev,
      shopify_stores: next,
      shopify_store: prev.shopify_store && next.includes(prev.shopify_store) ? prev.shopify_store : (next[0] || ""),
      store_fx_rates: fxCopy,
    }));
  };

  const removeShop = (shop) => {
    const next = (formData.shopify_stores || []).filter(s => s !== shop);
    updateFormStores(next);
  };

  const addAgencyShop = (shop) => {
    if (!shop) return;
    if ((formData.shopify_stores || []).includes(shop)) return;
    updateFormStores([...(formData.shopify_stores || []), shop]);
  };

  const addIndividualShop = () => {
    const raw = (newShop || "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
    if (!raw) return;
    const shop = raw.endsWith(".myshopify.com") ? raw : `${raw}.myshopify.com`;
    if ((formData.shopify_stores || []).includes(shop)) {
      setNewShop("");
      return;
    }
    updateFormStores([...(formData.shopify_stores || []), shop]);
    setNewShop("");
  };

  const switchMode = async (next) => {
    if (next === mode) return;
    setError(null);
    setMode(next);
    if (!isEdit) return; // no override doc yet — set on first save
    try {
      const enabled = next === "individual";
      await axios.put(`${API}/clients/${clientId}/shopify/credentials-override`, { enabled });
      await loadOverride();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to switch connection mode");
      setMode(mode); // revert
    }
  };

  const testConnection = async (shop) => {
    const token = (editingTokens[shop] || "").trim();
    if (!token) {
      setTestResult(p => ({ ...p, [shop]: { ok: false, msg: "Enter a token first" } }));
      return;
    }
    setTesting(p => ({ ...p, [shop]: true }));
    setTestResult(p => ({ ...p, [shop]: null }));
    try {
      const r = await axios.post(`${API}/clients/${clientId}/shopify/credentials-override/test`, { shop, token });
      if (r.data?.ok) {
        setTestResult(p => ({ ...p, [shop]: { ok: true, msg: `Connected — ${r.data.shop_name || shop}${r.data.currency ? " · " + r.data.currency : ""}` } }));
      } else {
        setTestResult(p => ({ ...p, [shop]: { ok: false, msg: `Failed (${r.data?.status || "?"}): ${(r.data?.detail || "").slice(0, 140)}` } }));
      }
    } catch (e) {
      setTestResult(p => ({ ...p, [shop]: { ok: false, msg: e.response?.data?.detail || "Request failed" } }));
    } finally {
      setTesting(p => ({ ...p, [shop]: false }));
    }
  };

  const saveTokens = async () => {
    setSaving(true);
    setError(null);
    try {
      const tokens = {};
      Object.entries(editingTokens).forEach(([shop, v]) => {
        if ((v || "").trim()) tokens[shop] = v.trim();
      });
      await axios.put(`${API}/clients/${clientId}/shopify/credentials-override`, {
        enabled: true,
        tokens,
      });
      setEditingTokens({});
      setTestResult({});
      await loadOverride();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeToken = async (shop) => {
    if (!window.confirm(`Remove individual token for ${shop}? This shop will have no token configured.`)) return;
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}/shopify/credentials-override`, { remove: [shop] });
      await loadOverride();
    } finally {
      setSaving(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    setSyncStatus(null);
    try {
      const today = new Date();
      const month = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
      await axios.post(`${API}/revenue/${clientId}/${month}/sync`);
      setSyncStatus({ ok: true, msg: `Synced revenue for ${month}` });
    } catch (e) {
      setSyncStatus({ ok: false, msg: e.response?.data?.detail || "Sync failed" });
    } finally {
      setSyncing(false);
    }
  };

  const selectedStores = formData.shopify_stores || [];
  const masked = overrideStatus?.tokens_masked || {};
  const updatedAt = overrideStatus?.updated_at;

  return (
    <div
      className="form-group md:col-span-2 p-4 bg-[#1A1A1A] border border-[#2A2A2A] rounded space-y-4"
      data-testid="shopify-connection-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <label className="form-label text-[#FFEB00] mb-1 block">Shopify Connection</label>
          <p className="text-[11px] text-[#71717A]">
            Choose how this client&apos;s Shopify revenue is pulled. Switch any time — only one mode is active at a time.
          </p>
        </div>
        {readOnly && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-white/[0.06] border border-white/10 text-white/60" data-testid="shopify-readonly-badge">
            Read-only · Super-admin only
          </span>
        )}
        {!readOnly && isEdit && updatedAt && (
          <div className="text-[10px] text-[#71717A]">
            Last updated: {new Date(updatedAt).toLocaleString()}
          </div>
        )}
      </div>

      {/* MODE TOGGLE — Top of panel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="shopify-mode-toggle">
        <button
          type="button"
          onClick={() => switchMode("agency")}
          disabled={loadingOverride || readOnly}
          className={`text-left p-3 rounded border transition-colors ${
            mode === "agency"
              ? "border-[#FFEB00] bg-[#1A1A00]"
              : "border-[#2A2A2A] hover:border-[#FFEB00]/40"
          } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
          data-testid="shopify-mode-agency"
        >
          <div className="flex items-center gap-2">
            <input type="radio" readOnly checked={mode === "agency"} className="accent-[#FFEB00]" />
            <span className="text-sm font-medium text-white">Agency Connection</span>
          </div>
          <p className="text-[11px] text-[#71717A] ml-6 mt-1">
            Use the agency-wide Shopify OAuth (default for most clients).
          </p>
        </button>

        <button
          type="button"
          onClick={() => switchMode("individual")}
          disabled={loadingOverride || readOnly}
          className={`text-left p-3 rounded border transition-colors ${
            mode === "individual"
              ? "border-[#FFEB00] bg-[#1A1A00]"
              : "border-[#2A2A2A] hover:border-[#FFEB00]/40"
          } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
          data-testid="shopify-mode-individual"
        >
          <div className="flex items-center gap-2">
            <input type="radio" readOnly checked={mode === "individual"} className="accent-[#FFEB00]" />
            <span className="text-sm font-medium text-white">Individual Login</span>
          </div>
          <p className="text-[11px] text-[#71717A] ml-6 mt-1">
            Use the client's own Shopify Admin API token (encrypted at rest).
          </p>
        </button>
      </div>

      {error && (
        <div className="text-xs p-2 rounded" style={{ background: "#FF444422", color: "#FF6666" }}>{error}</div>
      )}

      {/* ───────── AGENCY MODE ───────── */}
      {mode === "agency" && (
        <div className="space-y-3 pt-3 border-t border-[#2A2A2A]" data-testid="shopify-agency-section">
          <div className="space-y-2">
            {selectedStores.length === 0 && (
              <p className="text-[11px] text-[#71717A]">No stores added yet. Pick one from the agency pool below.</p>
            )}
            {selectedStores.map(s => (
              <div key={s} className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#00E676]/10 border border-[#00E676]/30 rounded text-[#00E676]">
                  {s}
                  <button type="button" onClick={() => removeShop(s)} className="hover:text-white ml-1" data-testid={`remove-store-${s}`}>&times;</button>
                </span>
                <div className="flex items-center gap-1">
                  <label className="text-xs text-[#71717A]">FX Rate:</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="input-default w-20 text-xs py-1 px-2"
                    value={(formData.store_fx_rates || {})[s] ?? 1}
                    onChange={e => setFormData(prev => ({
                      ...prev,
                      store_fx_rates: { ...(prev.store_fx_rates || {}), [s]: parseFloat(e.target.value) || 1 }
                    }))}
                    data-testid={`fx-rate-${s}`}
                  />
                  <span className="text-xs text-[#71717A]">→ {currency}</span>
                </div>
              </div>
            ))}
          </div>

          <select
            className="input-default w-full"
            onChange={e => { addAgencyShop(e.target.value); e.target.value = ""; }}
            data-testid="select-shopify-store"
          >
            <option value="">Add Shopify store from agency pool…</option>
            {agencyPool.filter(s => !selectedStores.includes(s.shop)).map(s => (
              <option key={s.shop} value={s.shop}>{s.shop}</option>
            ))}
          </select>
          <p className="text-[11px] text-[#71717A]">
            Revenue from all selected stores will be combined (FX-converted to {currency}).
          </p>
        </div>
      )}

      {/* ───────── INDIVIDUAL MODE ───────── */}
      {mode === "individual" && (
        <div className="space-y-3 pt-3 border-t border-[#2A2A2A]" data-testid="shopify-individual-section">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              placeholder="cordstudio.myshopify.com"
              value={newShop}
              onChange={e => setNewShop(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addIndividualShop(); } }}
              className="input-default flex-1 min-w-[260px] text-xs"
              data-testid="shopify-individual-shop-input"
            />
            <button
              type="button"
              onClick={addIndividualShop}
              className="btn-secondary text-xs px-3 py-1.5"
              data-testid="shopify-individual-add-shop"
            >
              + Add shop
            </button>
          </div>

          {selectedStores.length === 0 && (
            <p className="text-[11px] text-[#71717A]">
              Add a shop above, then paste the client's Shopify Admin API access token below.
            </p>
          )}

          {selectedStores.map((shop) => {
            const tokMasked = masked[shop];
            const newVal = editingTokens[shop] ?? "";
            const tr = testResult[shop];
            return (
              <div key={shop} className="p-3 rounded bg-[#0F0F0F] border border-[#2A2A2A] space-y-2" data-testid={`shopify-cred-row-${shop}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <code className="text-xs text-white">{shop}</code>
                  <div className="flex items-center gap-3 text-[11px]">
                    {tokMasked ? (
                      <>
                        <span className="text-[#71717A]">Saved:</span>
                        <code className="text-[#A1A1AA]">{tokMasked}</code>
                        <button onClick={() => removeToken(shop)} className="text-[10px] text-[#FF6666] hover:text-[#FF8080]" disabled={saving} data-testid={`shopify-cred-remove-${shop}`}>
                          Remove token
                        </button>
                      </>
                    ) : (
                      <span className="text-[#F59E0B]">No token saved yet</span>
                    )}
                    <button type="button" onClick={() => removeShop(shop)} className="text-[10px] text-[#FF6666] hover:text-[#FF8080]" data-testid={`remove-store-${shop}`}>
                      Remove shop
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="password"
                    placeholder={tokMasked ? "Enter new token to replace…" : "shpat_xxxxxxxxxxxx (Admin API access token)"}
                    value={newVal}
                    onChange={e => setEditingTokens(p => ({ ...p, [shop]: e.target.value }))}
                    className="input-default flex-1 min-w-[260px] text-xs"
                    data-testid={`shopify-cred-input-${shop}`}
                  />
                  <button
                    type="button"
                    onClick={() => testConnection(shop)}
                    disabled={testing[shop] || !newVal}
                    className="px-3 py-1.5 text-xs rounded border border-[#3A3A3A] text-[#A1A1AA] hover:text-white disabled:opacity-50"
                    data-testid={`shopify-cred-test-${shop}`}
                  >
                    {testing[shop] ? "Testing…" : "Test connection"}
                  </button>
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-[#71717A]">FX Rate:</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="input-default w-20 text-xs py-1 px-2"
                      value={(formData.store_fx_rates || {})[shop] ?? 1}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        store_fx_rates: { ...(prev.store_fx_rates || {}), [shop]: parseFloat(e.target.value) || 1 }
                      }))}
                      data-testid={`fx-rate-${shop}`}
                    />
                    <span className="text-xs text-[#71717A]">→ {currency}</span>
                  </div>
                </div>
                {tr && (
                  <div
                    className="text-[11px] px-2 py-1 rounded"
                    style={{ background: tr.ok ? "#10B98122" : "#FF444422", color: tr.ok ? "#6EE7B7" : "#FF8080" }}
                    data-testid={`shopify-cred-test-result-${shop}`}
                  >
                    {tr.msg}
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] text-[#71717A]">
              Tokens are encrypted at rest (Fernet) and masked in the UI.
            </div>
            <div className="flex items-center gap-2">
              {savedFlash && <span className="text-[11px] text-[#6EE7B7]" data-testid="shopify-cred-saved-flash">✓ Saved</span>}
              {Object.keys(editingTokens).some(k => (editingTokens[k] || "").trim()) && (
                <>
                  <button
                    type="button"
                    onClick={() => { setEditingTokens({}); setTestResult({}); }}
                    className="text-xs px-3 py-1.5 rounded border border-[#3A3A3A] text-[#A1A1AA]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveTokens}
                    disabled={saving}
                    className="btn-primary text-xs px-3 py-1.5"
                    data-testid="shopify-creds-save"
                  >
                    {saving ? "Saving…" : "Save credentials"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SYNC NOW — visible in both modes once we have at least one store */}
      {isEdit && selectedStores.length > 0 && (
        <div className="pt-3 border-t border-[#2A2A2A] flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] text-[#71717A]">
            After changing the connection, run a sync so this client's revenue appears on the dashboard.
          </div>
          <div className="flex items-center gap-2">
            {syncStatus && (
              <span
                className="text-[11px] px-2 py-1 rounded"
                style={{ background: syncStatus.ok ? "#10B98122" : "#FF444422", color: syncStatus.ok ? "#6EE7B7" : "#FF8080" }}
                data-testid="shopify-sync-status"
              >
                {syncStatus.msg}
              </span>
            )}
            <button
              type="button"
              onClick={syncNow}
              disabled={syncing}
              className="btn-secondary text-xs px-3 py-1.5"
              data-testid="shopify-sync-now"
            >
              {syncing ? "Syncing…" : "Sync revenue now"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopifyConnection;
